// Logic imports
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useConfirm } from "../../hooks/useConfirm";

import { Layout, Avatar, Dropdown, Menu, Tooltip, Input, Button, Drawer } from "antd";
import {
    SearchOutlined,
    QuestionCircleOutlined,
    GlobalOutlined,
    SunOutlined,
    MoonFilled,
    MenuOutlined,
    UserOutlined,
    LogoutOutlined,
    CrownOutlined, CommentOutlined, SisternodeOutlined, NotificationOutlined,
} from "@ant-design/icons";
import NotificationDrawer from "../Drawers/NotificationDrawer";
import { LogoMarkUpdated } from "../../vectorImages/LogoMarkUpdated";
import { Link } from "react-router-dom";
import { authActions } from "../../store";
import { useOrganization } from "../../context/organizationContext";

import { setAppLanguage, normalizeLang, detectedInitialLang } from "../../utils/LangUtils/langStorage"

const { Header } = Layout;

/**
 * Global header component with logo, search, navigation and theme switcher. The theme switcher is not active for now.
 * @param {Boolean} [initialDark = true] - boolean parameter to control the theme
 * @returns {Element} - adaptive header component.
 * @constructor
 */
export function ImeanHeader({ initialDark = true }) {
    const [dark, setDark] = useState(initialDark);
    const [searchOpen, setSearchOpen] = useState(false);
    const user = useSelector((obj) => obj.auth.user);
    const org = useOrganization();
    const isModerator = org?.organization?.moderatorExist && user?.moderatorOrg
    const { t } = useTranslation();

    useEffect(() => {
        const root = document.documentElement;
        if (dark) root.classList.add("dark");
        else root.classList.remove("dark");
    }, [dark]);

    return (
        <div className="w-full">
            <Header
                className="py-2 !px-3 sm:!px-4 lg:!px-6 !h-auto !leading-none shadow/10 bg-white text-slate-900 dark:!bg-[#1C1728] dark:!text-white"
                style={{ background: dark ? "#191528" : "#ffffff" }}
            >
                <HeaderBar>
                    <Brand />
                    <RightControls
                        user={user}
                        dark={dark}
                        onToggleDark={() => setDark((v) => !v)}
                        onOpenSearch={() => setSearchOpen(true)}
                        t={t}
                        isModerator={isModerator}
                    />
                </HeaderBar>
            </Header>

            <MobileSearchDrawer open={searchOpen} onClose={() => setSearchOpen(false)} />
        </div>
    );
}

/**
 * Element to control the navigation of the header component
 * @param {Element[]} children - a list of elements to be displayed in the header navigation
 * @returns {Element} - an element handling the navigation of the header
 * @constructor
 */
function HeaderBar({ children }) {
    return <div className="mx-5 flex items-center justify-between gap-2">{children}</div>;
}

/**
 * Element to display the brand logo of the page
 * @returns {Element} - Wrapper of a logo image. Clickable, navigating to the '/'.
 * @constructor
 */
function Brand() {
    return (
        <div className="flex items-center ">
            <Link to={'/'} className="flex items-center space-x-2 no-underline">
                <LogoMarkUpdated size={64} color="#fff" />
            </Link>
        </div>
    );
}

/**
 * Element to handle basic navigation
 * @param {Object} user - User object to handle profile and subscription status
 * @param {Boolean} dark - Boolean parameter to control interface theme
 * @param {function} onToggleDark - Function to execute on theme switch
 * @param {function} onOpenSearch - Function to execute on search activation
 * @param {TFunction<"translation", undefined>} t - i18n translator
 * @param {Boolean} isModerator - whether the user is moderator
 * @returns {Element} Navigation controls element (located on the right in the header)
 * @constructor
 */
function RightControls({ user, dark, onToggleDark, onOpenSearch, t, isModerator }) {
    return (
        <div className="flex items-center gap-2 sm:gap-3">
            <Notifications />
            {user ?
                <>
                    <Profile userName={user.name} translation={t} isModerator={isModerator}/>
                    <ActiveSubscription user={user} translation={t} />
                </> : null}
            <LanguageSwitcher />
        </div>
    );
}

/**
 * Element to handle quick actions (tips, search, etc.). Adjust as needed.
 * @param {function} onOpenSearch - method to handle search activation.
 * @returns {Element} - navigation flex element for quick actions
 * @constructor
 */
function QuickActions({ onOpenSearch, translation }) {
    return (
        <div className="hidden sm:flex items-center gap-2">
            <Tooltip title={translation("header.search")}>
                <IconButton icon={<SearchOutlined className="text-white" />} onClick={onOpenSearch} />
            </Tooltip>
            <Tooltip title={translation("header.help")}>
                <IconButton icon={<QuestionCircleOutlined className="text-white" />} />
            </Tooltip>
        </div>
    );
}

/**
 * Notifications wrapper for legacy notifications feature. Refactor if needed.
 * @returns {Element} - Icon to handle notifications management.
 * @constructor
 */
function Notifications() {
    return (
        <NotificationDrawer />
    );
}

/**
 * Element that displays a crown on active subscription. Otherwise, displays an empty div.
 * @param {Object} user - object containing the user data. The user data is stored in localstorage.
 * @returns {Element} - a crown icon if the subscription is active.
 * @constructor
 */
function ActiveSubscription({ user }) {
    const sid = user ? jwtDecode(JSON?.parse(localStorage?.getItem("user"))?.accessToken)?.subscriptionLevel?.id : false;
    return (
        <div>
            {!!sid && <CrownOutlined />}
        </div>
    )
}

/**
 * Logout behavior handler function
 * @param translation - i18n translation
 * @param dispatch - dispatch for handling logout
 */

async function Logout(translation, dispatch, confirm) {
    const ok = await confirm({
        title: translation("home.logoutTitle") || "Выход",
        message: translation("home.logoutConfirm") || "Вы уверены, что хотите выйти?",
        confirmText: translation("common.yes") || "Да",
        cancelText: translation("common.no") || "Нет",
    });

    if (ok) {
        dispatch(authActions.logout());
    }
}

/**
 * Profile component that displays a profile picture of the user, and their name
 * @param {String} userName - user's name
 * @param {function} translation - i18n locale translator
 * @returns {Element} - a collapsible menu of elements and actions
 * @constructor
 */
function Profile({ userName, translation, isModerator }) {
    const dispatch = useDispatch();
    const confirm = useConfirm();
    const menu = (
        <Menu
            items={[
                { key: "profile", icon: <UserOutlined />, label: <Link to={'/profile'}>{translation("home.profile")}</Link>, },
                ...(isModerator
                    ? [{
                        key: "alertsManagement",
                        icon: <NotificationOutlined />,
                        label: <Link to="/alerts-management">{translation("home.alertsManagement")}</Link>,
                    }]
                    : []),
                { key: 'support', icon: <CommentOutlined />, label: <Link to={'/support'}>{translation("home.support")}</Link> },
                { key: 'guideline', icon: <SisternodeOutlined />, label: <Link to={'/guideline'}>{translation("home.guideline")}</Link> },
                { type: "divider" },
                {
                    key: "logout", icon: <LogoutOutlined />, danger: true, label: <div onClick={() => Logout(translation, dispatch, confirm)}>{translation("home.logout")}</div>
                },
            ]}
        />
    );
    return (
        <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <div className="flex items-center gap-2 cursor-pointer">
                <Avatar size={28} src="https://api.dicebear.com/7.x/thumbs/svg?seed=imean" />
                <span className="hidden lg:inline text-sm text-white">{userName}</span>
            </div>
        </Dropdown>
    );
}

/**
 * An icon that handles the language changes. Checks the localstorage and changes the user's language.
 * @returns {Element} - a collapsible menu of available languages, an icon for header display.
 * @constructor
 */
function LanguageSwitcher() {
    const { i18n } = useTranslation();
    const current = normalizeLang(detectedInitialLang());

    const items = [
        { key: "ru", label: "Русский" },
        { key: "kk", label: "Қазақша" },
        { key: "en", label: "English" },
    ];

    return (
        <Dropdown
            menu={{
                items,
                selectedKeys: [current],
                onClick: ({ key }) => setAppLanguage(key),
            }}
            trigger={["click"]}
            placement="bottomRight"
        >
            <IconButton icon={<GlobalOutlined className="text-white" />} />
        </Dropdown>
    );
}

/**
 * Toggle button to switch between dark and light theme
 * @param {Boolean} dark - boolean parameter to control if the mode is dark
 * @param {function} onToggle - function parameter to control the app behavior on theme change
 * @returns {Element} - a clickable sun/moon icon to switch between light and dark theme
 * @constructor
 */
function ThemeToggle({ dark, onToggle }) {
    return (
        <Tooltip title={dark ? "Тёмная тема" : "Светлая тема"}>
            <IconButton icon={dark ? <MoonFilled className="text-white" /> : <SunOutlined className="text-white" />}
                onClick={onToggle} />
        </Tooltip>
    );
}

/**
 * To be displayed on mobile devices to avoid content overflow in the header.
 * @returns {Element} - clickable hamburger menu for mobile devices.
 * @constructor
 */
function Hamburger() {
    return <IconButton className="sm:hidden" icon={<MenuOutlined className="text-white" />} />;
}

/**
 * To be triggered on mobile devices in case the user wants to use search
 * @param {Boolean} open - boolean parameter to determine if the search is open
 * @param {function} onClose - function parameter to define on close behavior
 * @returns {Element} - a clickable search bar
 * @constructor
 */
function MobileSearchDrawer({ open, onClose }) {
    return (
        <Drawer title="Поиск" placement="top" height={80} open={open} onClose={onClose} bodyStyle={{ padding: 12 }}>
            <Input.Search allowClear enterButton placeholder="Начните вводить запрос…" />
        </Drawer>
    );
}

/**
 * Small building block for round icon buttons
 */
function IconButton({ icon, className = "", ...rest }) {
    return <Button shape="circle" type="text" className={className} icon={icon} {...rest} />;
}

/**
 * Brand logo component
 * @param {Number} size - size (square) in px
 * @param {String} color - color of the logo
 * @param {String} className - additional classes for layout
 * @param {Object} rest - any parameters to put into a svg component
 * @returns {Element} - a resizeable logo component
 * @constructor
 */
function LogoMark({ size = 32, color = "currentColor", className = "", ...rest }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="280 270 480 480" // tighter crop so artwork fills box
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-hidden="true"
            className={className}
            {...rest}
        >
            <path
                fill={color}
                d="M575.884399,684.963623 C546.195740,701.928528 514.607117,711.581116 480.926849,714.364929 C453.978851,716.592224 426.971069,714.788086 399.997192,715.368042 C397.313232,715.425659 395.245178,714.516174 395.038330,711.651917 C394.824921,708.697144 396.925842,707.613708 399.524353,707.421997 C402.013275,707.238403 404.511566,707.106018 407.006531,707.091919 C429.002686,706.967346 451.037384,707.669434 472.987518,706.610535 C522.687378,704.213135 566.793396,686.873962 604.738525,654.492432 C639.193176,625.089600 661.270752,588.140930 669.184631,543.553528 C679.579285,484.988098 666.137085,431.979736 627.970581,386.114624 C594.516174,345.912018 550.865295,323.643951 499.344910,316.162964 C488.753723,314.625061 478.152710,314.122009 467.482941,314.119354 C417.488464,314.107056 367.493988,314.049530 317.499512,313.992889 C310.211639,313.984619 310.031891,313.793121 309.996948,306.360046 C309.966400,299.861267 310.082520,293.361145 309.995544,286.863495 C309.940491,282.750671 311.504181,280.889832 315.897247,280.898773 C370.391052,281.009521 424.891541,280.490479 479.377014,281.182983 C522.530029,281.731445 563.529663,291.769867 600.890320,314.130249 C655.988220,347.106415 691.970703,394.725800 706.040405,457.401947 C721.840820,527.787842 706.301453,591.414246 660.306091,647.310242 C659.354248,648.466980 658.311096,649.549622 657.293579,650.651123 C655.452942,652.643677 653.331238,654.187622 650.891724,651.842712 C648.691833,649.728088 649.351685,647.395447 651.176758,645.184387 C655.416992,640.047302 659.797852,635.006470 663.737061,629.644104 C684.554260,601.305908 697.596008,569.779236 701.794434,534.788757 C710.198181,464.751404 689.423279,404.349762 640.025757,354.368835 C607.238159,321.194092 566.740784,301.749451 520.942078,293.352478 C504.365387,290.313263 487.626190,289.214844 470.763428,289.259552 C422.435608,289.387665 374.107269,289.297760 325.779114,289.296722 C318.329590,289.296570 318.301544,289.304443 318.335358,296.556183 C318.382690,306.709076 317.190521,305.681488 327.381317,305.688690 C376.042755,305.722992 424.715454,305.049438 473.362488,305.887207 C523.527588,306.751068 568.951111,322.235443 607.872620,354.606171 C648.339294,388.261902 672.591309,431.421631 678.948547,483.548706 C685.872070,540.319519 671.045349,591.574707 634.542053,635.962524 C618.211304,655.820740 598.485779,671.896729 575.884399,684.963623 z"
            />
            <path
                fill={color}
                d="M627.589478,595.493652 C597.923889,645.859192 554.107666,675.589111 497.607971,687.266785 C483.377350,690.208008 468.968842,691.304504 454.427826,691.254028 C421.601196,691.140076 388.773834,691.267029 355.946747,691.252014 C327.000336,691.238708 310.001831,674.330505 309.963867,645.196838 C309.881531,582.042358 309.948944,518.887634 309.960693,455.733002 C309.967712,418.073547 309.981415,380.414062 310.009491,342.754608 C310.010986,340.759247 310.108612,338.758331 310.281403,336.770386 C310.446503,334.871063 311.423126,333.700653 313.497620,333.724640 C314.829895,333.740051 316.162811,333.677826 317.495453,333.678009 C368.152496,333.684814 418.811615,333.416077 469.466003,333.788788 C513.404968,334.112061 553.097229,347.409515 586.994141,375.775696 C620.899902,404.149323 641.767334,440.332855 648.103821,484.250305 C653.748840,523.374695 646.939514,560.470947 627.589478,595.493652 M318.299286,383.499969 C318.300293,471.655182 318.390320,559.810547 318.234772,647.965454 C318.199402,668.016113 333.103149,683.285706 352.950897,682.932983 C388.435211,682.302307 423.938904,682.821228 459.434082,682.722046 C492.718750,682.629089 524.063538,674.853271 553.121216,658.417786 C618.063782,621.685608 652.303528,551.736206 638.716187,478.209686 C624.568176,401.649506 558.656555,346.869507 480.385162,342.597382 C447.315613,340.792419 414.078156,342.077240 380.917145,341.930176 C362.089691,341.846680 343.262360,341.731018 324.435394,341.575378 C320.337677,341.541504 318.016449,342.514496 318.171295,347.507965 C318.532684,359.162048 318.294769,370.834778 318.299286,383.499969 z"
            />
            <path
                fill={color}
                d="M450.999268,666.145020 C423.340820,666.158936 396.182098,666.221497 369.023621,666.169495 C352.387054,666.137634 340.933777,657.122375 337.269989,641.343140 C336.897247,639.737793 336.696594,638.054749 336.691772,636.406982 C336.428864,546.782532 336.226593,457.157928 335.877686,367.533875 C335.857849,362.438019 337.737640,360.924255 342.606903,360.937927 C384.260864,361.054749 425.923523,360.476685 467.567352,361.135437 C512.398926,361.844635 551.114380,377.655670 581.832764,411.082489 C602.013550,433.042542 614.270569,458.885193 618.339233,488.424042 C625.504456,540.443115 609.734619,584.834045 572.278198,621.217651 C544.471191,648.228271 510.491943,662.183838 471.957184,665.464783 C465.127930,666.046265 458.323120,666.076599 450.999268,666.145020 M507.697571,650.149475 C519.813599,646.283997 531.263550,641.002686 542.110901,634.341248 C605.688721,595.296997 629.662354,516.671082 598.942627,451.831726 C573.574036,398.286560 528.844727,371.986176 470.606354,369.651642 C430.681610,368.051239 390.641235,369.412628 350.653839,369.203552 C345.559448,369.176941 344.198090,370.661835 344.206512,375.698181 C344.350250,461.506500 344.295776,547.315125 344.317383,633.123718 C344.321259,648.595947 353.459595,657.739075 368.798462,657.741272 C398.456573,657.745544 428.114960,657.637756 457.772644,657.740967 C474.560852,657.799377 490.919189,655.285461 507.697571,650.149475 z"
            />
            <path
                fill={color}
                d="M370.842255,472.000000 C370.837097,448.012482 370.833069,424.524963 370.824310,401.037445 C370.819946,389.365265 375.467377,384.686096 387.153351,384.713226 C411.806793,384.770447 436.461945,384.703461 461.113159,384.977386 C487.951813,385.275574 513.240479,391.476898 535.927795,406.313141 C567.959106,427.259857 587.437134,456.519867 592.325806,494.656921 C597.490601,534.948120 585.806702,570.295654 557.757935,599.513916 C535.331055,622.875916 507.634216,636.797119 475.012451,639.318115 C444.593781,641.668823 414.086670,639.933899 383.620911,640.246460 C377.049286,640.313965 372.786285,636.374573 371.422638,629.863403 C371.017639,627.929565 370.912048,625.903198 370.910370,623.919067 C370.867859,573.446045 370.857422,522.973022 370.842255,472.000000 M427.215332,561.500000 C427.215546,566.995422 427.208038,572.490845 427.217529,577.986267 C427.239441,590.677368 427.245880,590.851013 439.730560,590.627319 C452.348206,590.401306 465.053833,591.908997 477.565216,589.206909 C528.386963,578.230896 555.128906,520.295044 529.875244,474.783386 C517.387329,452.277832 498.159119,438.419556 472.082306,435.110657 C460.463379,433.636322 448.800018,434.674469 437.159363,434.457184 C427.531433,434.277466 427.210968,434.719940 427.211517,444.096893 C427.213745,482.897919 427.217987,521.698975 427.215332,561.500000 z"
            />
        </svg>
    );
}
